/**
 * Ad-hoc custom workout builder. Ported from the Next app's
 * workouts/custom/page.tsx. Lets a client add exercises (via ExercisePicker),
 * log sets, mark them complete, and save to POST /api/workouts/logs with
 * workout_type:'custom'. Luxury design system.
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Dumbbell, Plus, Minus, Check, X, Timer } from 'lucide-react';
import { ExercisePicker } from '@/components/ExercisePicker';
import { api } from '@/lib/api';

interface SetData { setNumber: number; weight: string; reps: string; rpe: string; completed: boolean; }
interface ExerciseData { exerciseId: string; exerciseName: string; sets: SetData[]; }

const emptySet = (n: number): SetData => ({ setNumber: n, weight: '', reps: '', rpe: '', completed: false });

export default function CustomWorkout() {
  const navigate = useNavigate();
  const [workoutName, setWorkoutName] = useState('Custom Workout');
  const [exercises, setExercises] = useState<ExerciseData[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const startRef = useRef(Date.now());

  function addExercise(ex: { id: string; name: string }) {
    setExercises((prev) => [...prev, { exerciseId: ex.id, exerciseName: ex.name, sets: [emptySet(1)] }]);
  }
  function removeExercise(index: number) {
    setExercises((prev) => prev.filter((_, i) => i !== index));
  }
  function addSet(exIndex: number) {
    setExercises((prev) => {
      const next = [...prev];
      const sets = [...next[exIndex].sets];
      sets.push(emptySet(sets.length + 1));
      next[exIndex] = { ...next[exIndex], sets };
      return next;
    });
  }
  function removeSet(exIndex: number) {
    setExercises((prev) => {
      const next = [...prev];
      next[exIndex] = { ...next[exIndex], sets: next[exIndex].sets.slice(0, -1) };
      return next;
    });
  }
  function updateSet(exIndex: number, setIndex: number, field: 'weight' | 'reps' | 'rpe', value: string) {
    setExercises((prev) => {
      const next = [...prev];
      next[exIndex] = { ...next[exIndex], sets: [...next[exIndex].sets] };
      next[exIndex].sets[setIndex] = { ...next[exIndex].sets[setIndex], [field]: value };
      return next;
    });
  }
  function toggleComplete(exIndex: number, setIndex: number) {
    setExercises((prev) => {
      const next = [...prev];
      next[exIndex] = { ...next[exIndex], sets: [...next[exIndex].sets] };
      const s = next[exIndex].sets[setIndex];
      next[exIndex].sets[setIndex] = { ...s, completed: !s.completed };
      return next;
    });
  }

  const completedSets = exercises.reduce((sum, ex) => sum + ex.sets.filter((s) => s.completed).length, 0);

  async function handleFinish() {
    setSaving(true);
    const durationMinutes = Math.round((Date.now() - startRef.current) / 60000);
    try {
      await api.post('/api/workouts/logs', {
        workout_type: 'custom',
        duration_minutes: durationMinutes,
        notes: workoutName,
        exercises: exercises.map((ex) => ({
          exercise_id: ex.exerciseId,
          sets: ex.sets
            .filter((s) => s.completed)
            .map((s) => ({
              set_number: s.setNumber,
              actual_reps: s.reps ? parseInt(s.reps) : null,
              actual_weight: s.weight ? parseFloat(s.weight) : null,
              rpe: s.rpe ? parseInt(s.rpe) : null,
              is_warmup: false,
            })),
        })),
      });
      setSaved(true);
    } catch {
      /* keep form for retry */
    } finally {
      setSaving(false);
    }
  }

  if (saved) {
    return (
      <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Check size={64} style={{ color: 'var(--success)' }} />
        <h1 className="display mt-4" style={{ fontSize: '2.1rem' }}>
          Workout Saved
        </h1>
        <button onClick={() => navigate('/workouts')} className="btn-gold w-full mt-6">
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="pb-4">
      <div
        className="sticky top-0 z-20 px-5 pt-[max(16px,env(safe-area-inset-top))] pb-3"
        style={{ background: 'var(--ink)' }}
      >
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} style={{ color: 'var(--champagne)' }} aria-label="Back">
            <ArrowLeft size={22} />
          </button>
          <p className="text-sm" style={{ color: 'var(--cream-muted)' }}>{completedSets} sets</p>
          <span
            className="text-xs px-2 py-1 inline-flex items-center"
            style={{ borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--cream-muted)' }}
          >
            <Timer size={10} className="mr-1" />
            {Math.round((Date.now() - startRef.current) / 60000)} min
          </span>
        </div>
      </div>

      <div className="px-5">
        <input
          type="text"
          className="input mb-5"
          value={workoutName}
          onChange={(e) => setWorkoutName(e.target.value)}
          style={{ background: 'transparent', border: 'none', padding: 0, fontFamily: 'var(--font-display), Georgia, serif', fontSize: '1.7rem', color: 'var(--cream)' }}
        />

        {exercises.length === 0 ? (
          <div className="card text-center py-10 mb-4">
            <Dumbbell size={40} style={{ color: 'var(--cream-muted)' }} className="mx-auto mb-3" />
            <p className="font-semibold" style={{ color: 'var(--cream)' }}>No exercises yet</p>
            <p className="text-xs mt-1" style={{ color: 'var(--cream-muted)' }}>Add exercises to start your workout</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 mb-4">
            {exercises.map((ex, exIndex) => (
              <div key={exIndex} className="card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Dumbbell size={16} style={{ color: 'var(--champagne)' }} />
                    <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>{ex.exerciseName}</p>
                  </div>
                  <button onClick={() => removeExercise(exIndex)} style={{ color: 'var(--error)' }} aria-label="Remove exercise">
                    <X size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-[32px_1fr_1fr_48px_32px] gap-2 text-xs font-medium mb-1" style={{ color: 'var(--cream-muted)' }}>
                  <span>SET</span><span>LBS</span><span>REPS</span><span>RPE</span><span></span>
                </div>
                {ex.sets.map((set, si) => (
                  <div key={si} className="grid grid-cols-[32px_1fr_1fr_48px_32px] gap-2 items-center mb-1.5">
                    <span className="text-xs text-center" style={{ color: 'var(--cream-muted)' }}>{set.setNumber}</span>
                    <input type="number" inputMode="decimal" className="input text-center text-sm py-1.5 px-1" placeholder="0" value={set.weight} onChange={(e) => updateSet(exIndex, si, 'weight', e.target.value)} />
                    <input type="number" inputMode="numeric" className="input text-center text-sm py-1.5 px-1" placeholder="0" value={set.reps} onChange={(e) => updateSet(exIndex, si, 'reps', e.target.value)} />
                    <input type="number" inputMode="numeric" className="input text-center text-sm py-1.5 px-1" placeholder="-" value={set.rpe} onChange={(e) => updateSet(exIndex, si, 'rpe', e.target.value)} />
                    <button
                      onClick={() => toggleComplete(exIndex, si)}
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{
                        background: set.completed ? 'var(--success)' : 'transparent',
                        border: set.completed ? 'none' : '1.5px solid var(--cream-muted)',
                        color: set.completed ? 'white' : 'var(--cream-muted)',
                      }}
                      aria-label={set.completed ? 'Mark set incomplete' : 'Mark set complete'}
                    >
                      {set.completed && <Check size={14} strokeWidth={2.5} />}
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <button onClick={() => addSet(exIndex)} className="flex items-center gap-1 text-xs py-1.5 px-3 border" style={{ borderRadius: 'var(--radius-sm)', borderColor: 'var(--champagne-muted)', color: 'var(--champagne)' }}>
                    <Plus size={12} /> Add Set
                  </button>
                  {ex.sets.length > 1 && (
                    <button onClick={() => removeSet(exIndex)} className="flex items-center gap-1 text-xs py-1.5 px-3 border" style={{ borderRadius: 'var(--radius-sm)', borderColor: 'var(--ink-3)', color: 'var(--cream-muted)' }}>
                      <Minus size={12} /> Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <button onClick={() => setShowPicker(true)} className="btn-outline w-full flex items-center justify-center gap-2 mb-4">
          <Plus size={16} /> Add Exercise
        </button>

        <ExercisePicker
          title="Add Exercise"
          isOpen={showPicker}
          onClose={() => setShowPicker(false)}
          onSelect={(ex) => addExercise(ex)}
        />

        {exercises.length > 0 && (
          <button onClick={handleFinish} className="btn-gold w-full" disabled={completedSets === 0 || saving}>
            {saving ? 'Saving…' : `Finish Workout (${completedSets} sets)`}
          </button>
        )}
      </div>
    </div>
  );
}
